import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  AlertTriangle, CheckCircle2, ArrowRight, Search, 
  Stethoscope, Wrench, Rocket, Globe, Zap, Shield, 
  Users, PhoneCall, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const problems = [
  { text: 'Not listed or linked on Google', icon: AlertTriangle },
  { text: 'Slow or broken', icon: AlertTriangle },
  { text: 'Outdated or hard to trust', icon: AlertTriangle },
  { text: 'Not mobile-friendly', icon: AlertTriangle },
  { text: 'Missing clear calls-to-action', icon: AlertTriangle },
];

const solutions = [
  'Repair broken or down websites',
  'Redesign outdated pages for modern credibility',
  'Optimize for mobile and speed',
  'Improve structure for better conversions',
  'Rebuild entirely if necessary',
];

const processSteps = [
  { 
    step: 1, 
    title: 'Audit', 
    description: 'We analyze your website and identify what\'s costing you customers.',
    icon: Search
  },
  { 
    step: 2, 
    title: 'Diagnose', 
    description: 'We pinpoint the exact issues: performance, design, structure, or functionality.',
    icon: Stethoscope
  },
  { 
    step: 3, 
    title: 'Fix & Optimize', 
    description: 'We repair, redesign, and improve what matters most.',
    icon: Wrench
  },
  { 
    step: 4, 
    title: 'Deploy', 
    description: 'Your updated site goes live, clean, fast, and ready to convert.',
    icon: Rocket
  },
];

const outcomes = [
  { text: 'A website that actually works', icon: Globe },
  { text: 'Stronger first impressions', icon: Shield },
  { text: 'Faster load times', icon: Zap },
  { text: 'More trust from customers', icon: Users },
  { text: 'More calls, bookings, and conversions', icon: PhoneCall },
];

const websiteRescueSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Website Rescue",
  "description": "Fast website repair, redesign, and optimization services. We fix broken, slow, or outdated websites quickly.",
  "provider": {
    "@type": "Organization",
    "name": "Fearless IT",
    "url": "https://fearlessit.io"
  },
  "areaServed": "US",
  "serviceType": "Website Repair and Optimization"
};

const WebsiteRescuePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="website-rescue-page">
      <SEO 
        title="Website Rescue | Fearless IT"
        description="Is your website costing you customers? Fearless IT repairs, redesigns, and optimizes websites fast. No long contracts, just results."
        keywords="website repair, fix broken website, website rescue, website redesign, slow website fix, website optimization, website not working"
        url="/solutions/website-rescue"
        structuredData={websiteRescueSchema}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 sm:pt-28 sm:pb-24 bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#1e1b4b] overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hero-title">
            Your Website Shouldn't Be Costing You Customers
          </h1>
          <p className="text-xl sm:text-2xl text-indigo-200 mb-10 max-w-3xl mx-auto leading-relaxed">
            If your site is down, outdated, slow, or not converting, we fix it fast. No long timelines. No fluff. Just results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/growth-audit">
              <Button className="bg-indigo-500 hover:bg-indigo-600 text-white px-10 py-5 text-lg font-semibold rounded-xl transition-all w-full sm:w-auto">
                Get a Free Website Audit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/consultation">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-5 text-lg font-semibold rounded-xl transition-all w-full sm:w-auto">
                Fix My Website Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 sm:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-6">
                Most Businesses Don't Realize Their Website Is Losing Them Money
              </h2>
              <p className="text-lg text-slate-gray mb-8 leading-relaxed">
                You might not see it, but your customers do.
              </p>
              <p className="text-lg text-slate-gray mb-6">
                If your website is:
              </p>
              <div className="space-y-4">
                {problems.map((problem, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    </div>
                    <span className="text-carbon-black font-medium">{problem.text}</span>
                  </div>
                ))}
              </div>
              <p className="text-lg text-slate-gray mt-8 leading-relaxed">
                <strong className="text-carbon-black">You're losing real customers every single day.</strong> And in today's market, people judge your business in seconds.
              </p>
            </div>
            
            {/* Visual Accent */}
            <div className="hidden lg:flex items-center justify-center">
              <div className="relative">
                <div className="w-64 h-64 rounded-full bg-red-100 flex items-center justify-center">
                  <div className="w-48 h-48 rounded-full bg-red-200 flex items-center justify-center">
                    <div className="w-32 h-32 rounded-full bg-red-300 flex items-center justify-center">
                      <AlertTriangle className="h-16 w-16 text-red-600" />
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 w-12 h-12 rounded-full bg-red-400 animate-pulse"></div>
                <div className="absolute -bottom-2 -left-6 w-8 h-8 rounded-full bg-red-500 animate-pulse delay-300"></div>
                <div className="absolute top-1/2 -right-8 w-6 h-6 rounded-full bg-red-300 animate-pulse delay-500"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-2xl p-8 sm:p-12 border-l-4 border-indigo-500">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-6">
              We Fix What's Broken. Fast.
            </h2>
            <p className="text-lg text-slate-gray mb-8 leading-relaxed">
              At Fearless IT, we don't "manage" your website. We step in, identify the issue, and fix it quickly and cleanly.
            </p>
            <p className="text-lg text-slate-gray mb-6">
              Depending on what you need, we:
            </p>
            <div className="space-y-4 mb-8">
              {solutions.map((solution, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-carbon-black font-medium">{solution}</span>
                </div>
              ))}
            </div>
            <p className="text-lg text-slate-gray leading-relaxed">
              <strong className="text-indigo-600">No long contracts. No drawn-out timelines.</strong> Just a working, high-performing website.
            </p>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 sm:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              Simple. Fast. Effective.
            </h2>
          </div>
          
          {/* Desktop: Horizontal Stepper */}
          <div className="hidden md:block">
            <div className="relative">
              {/* Connecting Line */}
              <div className="absolute top-12 left-0 right-0 h-1 bg-indigo-200 mx-24"></div>
              
              <div className="grid grid-cols-4 gap-6">
                {processSteps.map((step, idx) => {
                  const IconComponent = step.icon;
                  return (
                    <div key={idx} className="relative flex flex-col items-center text-center">
                      <div className="w-24 h-24 rounded-full bg-indigo-500 flex items-center justify-center mb-6 relative z-10 shadow-lg">
                        <div className="text-center">
                          <span className="text-2xl font-bold text-white">{step.step}</span>
                        </div>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                        <IconComponent className="h-6 w-6 text-indigo-600" />
                      </div>
                      <h3 className="text-xl font-bold text-carbon-black mb-2">{step.title}</h3>
                      <p className="text-sm text-slate-gray leading-relaxed">{step.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Mobile: Vertical Stepper */}
          <div className="md:hidden space-y-6">
            {processSteps.map((step, idx) => {
              const IconComponent = step.icon;
              return (
                <div key={idx} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-lg font-bold text-white">{step.step}</span>
                    </div>
                    {idx < processSteps.length - 1 && (
                      <div className="w-0.5 h-full bg-indigo-200 my-2"></div>
                    )}
                  </div>
                  <div className="pb-8">
                    <div className="flex items-center gap-3 mb-2">
                      <IconComponent className="h-5 w-5 text-indigo-600" />
                      <h3 className="text-lg font-bold text-carbon-black">{step.title}</h3>
                    </div>
                    <p className="text-slate-gray">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Outcomes Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              What You Can Expect
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {outcomes.map((outcome, idx) => {
              const IconComponent = outcome.icon;
              return (
                <div key={idx} className="bg-white rounded-xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 flex items-center justify-center mb-4">
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-carbon-black">{outcome.text}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#1e1b4b] relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-indigo-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Get Back Online. The Right Way.
          </h2>
          <p className="text-xl text-indigo-200 mb-10 max-w-2xl mx-auto leading-relaxed">
            If your website isn't helping your business grow, it's holding you back. Let's fix it.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/growth-audit">
              <Button className="bg-white text-indigo-600 hover:bg-gray-100 px-10 py-5 text-lg font-semibold rounded-xl transition-all w-full sm:w-auto">
                Get Your Free Website Audit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/consultation">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-5 text-lg font-semibold rounded-xl transition-all w-full sm:w-auto">
                <Calendar className="mr-2 h-5 w-5" />
                Book a Quick Call
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default WebsiteRescuePage;
